import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../../core/data/routes';
import { useAuthStore } from '../../auth/store/authStore';
import { AppBar, LoadingWidget, Dialog, FadeInAnimation } from '../../../core/components/ui';
import * as api from '../../../core/api/services';
import toast from 'react-hot-toast';

// ── Delete Account Page ────────────────────────────────────────
export const DeleteAccountPage: React.FC = () => {
  const navigate = useNavigate();
  const authStore = useAuthStore();
  const user = authStore.retrieveUser();

  const [reason, setReason] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const reasons = [
    'No longer need therapy services',
    'Found another provider',
    'Privacy concerns',
    'Technical issues',
    'Too expensive',
    'Other',
  ];

  const handleDelete = async () => {
    if (!reason) { toast.error('Please select a reason.'); return; }
    setLoading(true);
    try {
      await api.deleteAccount({ userId: user?.id, reason });
      authStore.logout();
      navigate(ROUTES.DELETED_ACCOUNT, { replace: true });
    } catch {
      toast.error('Account deletion failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page">
      {loading && <LoadingWidget />}
      <AppBar title="Delete Account" onBack={() => navigate(-1)} />

      <div style={{ flex: 1, overflowY: 'auto', padding: '20px 20px 40px', display: 'flex', flexDirection: 'column', gap: 20 }}>

        <FadeInAnimation delay={0}>
          <div style={{
            padding: '20px', borderRadius: 16,
            background: 'var(--color-error-container)',
            display: 'flex', gap: 16, alignItems: 'flex-start',
          }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--color-error)" strokeWidth="2">
              <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
              <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--color-error)', marginBottom: 4 }}>
                This action cannot be undone
              </div>
              <p style={{ fontSize: 13, color: 'var(--color-on-error-container)', lineHeight: 1.5 }}>
                Deleting your account will permanently remove all your data, session history, and credits. You will not be able to recover this information.
              </p>
            </div>
          </div>
        </FadeInAnimation>

        <FadeInAnimation delay={0.1}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12 }}>
              Please tell us why you're leaving
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {reasons.map(r => (
                <button
                  key={r}
                  onClick={() => setReason(r)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '12px 16px', borderRadius: 10, width: '100%', textAlign: 'left',
                    background: reason === r ? 'var(--color-error-container)' : 'var(--color-surface)',
                    border: reason === r ? '2px solid var(--color-error)' : '2px solid var(--color-outline-variant)',
                    fontSize: 14, color: reason === r ? 'var(--color-on-error-container)' : 'var(--color-on-surface)',
                    fontWeight: reason === r ? 600 : 400, transition: 'all 0.15s',
                  }}
                >
                  <div style={{
                    width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
                    border: `2px solid ${reason === r ? 'var(--color-error)' : 'var(--color-outline)'}`,
                    background: reason === r ? 'var(--color-error)' : 'transparent',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {reason === r && <div style={{ width: 7, height: 7, borderRadius: '50%', background: 'white' }} />}
                  </div>
                  {r}
                </button>
              ))}
            </div>
          </div>
        </FadeInAnimation>

        <FadeInAnimation delay={0.2}>
          <button
            onClick={() => { if (!reason) { toast.error('Please select a reason.'); return; } setConfirmOpen(true); }}
            style={{
              width: '100%', height: 50, borderRadius: 8,
              background: 'var(--color-error)',
              color: 'white', fontWeight: 700, fontSize: 15,
            }}
          >
            Delete My Account
          </button>
        </FadeInAnimation>
      </div>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 8 }}>⚠️</div>
          <h3 style={{ fontSize: 17, fontWeight: 700, marginBottom: 8 }}>Are you absolutely sure?</h3>
          <p style={{ fontSize: 13, color: 'var(--color-on-surface-variant)', marginBottom: 20, lineHeight: 1.5 }}>
            Your account and all associated data will be permanently deleted. This cannot be undone.
          </p>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={() => setConfirmOpen(false)} style={{ flex: 1, height: 44, borderRadius: 8, background: 'var(--color-surface-variant)', color: 'var(--color-on-surface-variant)', fontWeight: 600 }}>
              Cancel
            </button>
            <button onClick={handleDelete} style={{ flex: 1, height: 44, borderRadius: 8, background: 'var(--color-error)', color: 'white', fontWeight: 700 }}>
              Delete
            </button>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

// ── Deleted Account Confirmation Page ─────────────────────────
export const DeletedAccountPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="page" style={{ background: 'var(--color-bg)', minHeight: '100vh' }}>
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 32px',
        textAlign: 'center',
        gap: 16,
      }}>
        <FadeInAnimation delay={0}>
          <div style={{ fontSize: 80 }}>👋</div>
        </FadeInAnimation>
        <FadeInAnimation delay={0.2}>
          <h1 style={{ fontSize: 22, fontWeight: 700 }}>Account Deleted</h1>
        </FadeInAnimation>
        <FadeInAnimation delay={0.3}>
          <p style={{ fontSize: 14, color: 'var(--color-on-surface-variant)', lineHeight: 1.6, maxWidth: 300 }}>
            Your account has been permanently deleted. We're sorry to see you go. If you change your mind, you can always create a new account.
          </p>
        </FadeInAnimation>
        <FadeInAnimation delay={0.5}>
          <button
            className="btn-primary"
            style={{ width: 200, marginTop: 16 }}
            onClick={() => navigate(ROUTES.LOGIN, { replace: true })}
          >
            Back to Login
          </button>
        </FadeInAnimation>
      </div>
    </div>
  );
};
